import React, { useMemo } from 'react';
import { MessageDescriptor, useIntl } from 'react-intl';
import { MusicAPIIntegrationID } from 'fym-shared/src/music-services/ids';
import { DistributorID } from 'fym-shared/src/smartlinks/catalog';
import { TypedMemo } from '../../common/types';
import { SMLButton } from '../components/primitives/SMLButton';
import { sourceLook } from './streamingSources';
import { streamingTotalLabels } from './streamingTotalsMessages';
import { streamingStateMessages as messages } from './streamingStateMessages';
import { SourceMark } from './parts/SourceMark';
import { Panel, PanelBody, PanelHead } from '../components/primitives/Panel';
import { withBrandWord } from '../components/primitives/BrandWord';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for the whole screen; each part uses its own slice of it */
import styles from './Streaming.module.scss';
/* eslint-enable css-modules/no-unused-class */

/** What each source gives (design f2). Information only: the cards have no control. */
const SOURCE_GIVES: Array<{ integrationType: string; kind: MessageDescriptor; gives: MessageDescriptor[] }> = [
  {
    integrationType: MusicAPIIntegrationID.Spotify,
    kind: messages.kindPortal,
    gives: [
      streamingTotalLabels.streams,
      streamingTotalLabels.listeners,
      streamingTotalLabels.saves,
      streamingTotalLabels.playlistAdds,
      streamingTotalLabels.followers,
    ],
  },
  {
    integrationType: MusicAPIIntegrationID.Deezer,
    kind: messages.kindPortal,
    gives: [streamingTotalLabels.streams, streamingTotalLabels.listeners, streamingTotalLabels.followers],
  },
  {
    integrationType: MusicAPIIntegrationID.YoutubeMusic,
    kind: messages.kindChannel,
    gives: [messages.givesViews, streamingTotalLabels.subscribers],
  },
  {
    integrationType: MusicAPIIntegrationID.SoundCloud,
    kind: messages.kindPortal,
    gives: [streamingTotalLabels.streams, streamingTotalLabels.followers],
  },
  {
    integrationType: DistributorID.Ditto,
    kind: messages.kindDistributor,
    gives: [
      streamingTotalLabels.streams,
      messages.givesStores,
      messages.givesCountries,
      messages.givesPlaylists,
      messages.givesEarnings,
    ],
  },
];

interface StreamingNoSourceProps {
  onOpenCatalog: () => void;
}

/** No source connected yet (design f2): one link button to Catalog and the information cards. */
const StreamingNoSourceComponent: React.FC<StreamingNoSourceProps> = ({ onOpenCatalog }) => {
  const intl = useIntl();
  const cards = useMemo(
    () =>
      SOURCE_GIVES.map((card) => (
        <div key={card.integrationType} className={styles.infoCard}>
          <div className={styles.infoCardHead}>
            <SourceMark integrationType={card.integrationType} />
            <span>{sourceLook(card.integrationType).name}</span>
          </div>
          <span className={styles.faint}>{intl.formatMessage(card.kind)}</span>
          <div className={styles.gives}>
            {card.gives.map((give) => (
              <span key={give.id}>{intl.formatMessage(give)}</span>
            ))}
          </div>
        </div>
      )),
    [intl]
  );

  return (
    <div className={styles.page} data-testid="streaming-no-source">
      <Panel as="section">
        <PanelHead titleAs="h2" title={intl.formatMessage(messages.noSourceTitle)} />
        <PanelBody className={styles.noSourceBody}>
          <p>{withBrandWord(intl.formatMessage(messages.noSourceText))}</p>
          <SMLButton variant="primary" onClick={onOpenCatalog}>
            {intl.formatMessage(messages.noSourceButton)}
          </SMLButton>
        </PanelBody>
      </Panel>
      <h3 className={styles.blockTitle}>{intl.formatMessage(messages.whatEachGives)}</h3>
      <div className={styles.infoCards}>{cards}</div>
    </div>
  );
};

export const StreamingNoSource = TypedMemo(StreamingNoSourceComponent);
